import { Box, Stack, styled } from '@mui/material';
import { Project } from '@provision/entity-types';
import { portalPaths } from '@provision/portal-routes';
import {
  memo,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useResizable } from 'react-resizable-layout';
import {
  BlockContainer,
  BlockContent,
  BlockHeader,
  CollapsingLayoutBlock,
  DynamicIcon,
  EntityOpenModeSwitcher,
  SidePanelTabs,
  SiteAddress,
  Tooltip,
} from 'src/components';
import { DeletedRecordAlert } from 'src/components/DeletedRecordAlert';
import { ExpandedConversations } from 'src/components/EntityFullPageTabs/expanded-conversations';
import {
  FULL_PAGE_CONTENT_COLUMN_SX,
  getEntityDetailsRailSx,
  getPanelToolsSlotSx,
  RIGHT_PANEL_RESIZE_GUTTER_WIDTH,
  SIDE_PANEL_TABS_FULL_PAGE_INNER_STYLES,
} from 'src/components/EntitySidePanel';
import { DrawerCloseControl } from 'src/components/Navbar/DesktopDrawer';
import { ResizeHandle } from 'src/components/ResizeHandle';
import { MembershipInformation } from 'src/components/SidePanelTabs/MembershipInformation/membership-information';
import { Projects } from 'src/components/SidePanelTabs/Projects/projects';
import { SiteActivityTab } from 'src/components/SidePanelTabs/site-acitivity-tab/site-activity-tab';
import { SiteClients } from 'src/components/SidePanelTabs/SiteClient/site-clients';
import { SiteDocumentation } from 'src/components/SidePanelTabs/SiteDocumentation/site-documentation';
import { SiteTabs } from 'src/components/SidePanelTabs/SiteTab/site-const';
import { useSiteTabs } from 'src/components/SidePanelTabs/SiteTab/use-site-tabs';
import { SiteMenu } from 'src/components/SiteComponents/site-menu';
import { ViewerListStateful } from 'src/components/ViewerListStateful/viewer-list-stateful';
import { useGroupRules, UserGroupRules } from 'src/graphql';
import { useRestoreAddress } from 'src/graphql/entities/address';
import { Address } from 'src/graphql/entities/address/address';
import { RecordOpenMode } from 'src/graphql/entities/user-setting-value/user-setting-value.types';
import { EntityNames } from 'src/graphql/types';
import {
  RIGHT_PANEL_MAX_WIDTH_PX,
  RIGHT_PANEL_MIN_WIDTH_PX,
  RIGHT_PANEL_WIDTH_PX,
} from 'src/pages/Conversations/conversation';
import { GridBreadcrumbItem } from 'src/pages/CustomViews/custom-views-selected-view/base-selected-view';
import { SiteSidePanel } from 'src/pages/Sites/SidePanel/side-panel';
import { SiteOverview } from 'src/pages/Sites/SiteOverview';
import useSiteViewsStore from 'src/store/site-views';
import { useCopyToClipboard, useCustomView } from 'src/utils/hooks';
import { TabType, useGlobalTabs } from 'src/utils/hooks/use-global-tabs';
import { useNavigateRoute } from 'src/utils/routing';
import { useScreenDimensions } from 'src/utils/system';

const OPEN_DETAILS_PANEL_TOOLTIP = 'Open Site Details';
const CLOSE_DETAILS_PANEL_TOOLTIP = 'Close Site Details';

const SiteContainer = styled(Stack, {
  name: 'SiteContainer',
})(({ theme }) => ({
  position: 'relative',
  flexDirection: 'row',
  flex: 1,
  zIndex: theme.zIndex.app.ServiceDeskConversation,
  width: 0,
}));

const SiteDetailsWrapper = styled(CollapsingLayoutBlock, {
  name: 'SiteDetailsWrapper',
})(({ theme, open }) => ({
  position: 'static',
  [theme.breakpoints.down('sm')]: {
    position: 'absolute',
    top: 0,
    height: '100%',
    right: -1,
    ...(!open ? {} : { right: 1 }),
  },
}));

const SidePanelTabsInnerStyles = {
  padding: 0,
};

const SidePanelTabStyles = {
  fontSize: '0.75rem',
};

interface TabContentProps {
  address: Partial<Address>;
  activeTab: SiteTabs;
  project?: Project;
}

const TabContent = ({ activeTab, address, project }: TabContentProps) => {
  const conversationsViewFilters = useMemo(
    () =>
      address.addressId
        ? [
            {
              attribute: 'associatedAddresses/addressId',
              operator: '=',
              value: address.addressId,
            },
          ]
        : [],
    [address.addressId],
  );

  switch (true) {
    case activeTab === SiteTabs.SiteDocumentation:
      return <SiteDocumentation address={address} hasShowMore={false} fullSizeView />;

    case activeTab === SiteTabs.Conversations && !!address.addressId:
      return (
        <ExpandedConversations
          viewFilters={conversationsViewFilters}
          customViewType={EntityNames.Site}
          viewId="siteConversations"
        />
      );

    case activeTab === SiteTabs.Clients:
      return (
        <Box p={2}>
          <SiteClients address={address} />
        </Box>
      );

    case activeTab === SiteTabs.MembershipInformation:
      return (
        <Box p={2}>
          <MembershipInformation address={address} />
        </Box>
      );

    case activeTab === SiteTabs.AiSummary && !!address.addressId:
      return (
        <Box p={2}>
          <SiteOverview addressId={address.addressId} />
        </Box>
      );

    case activeTab === SiteTabs.Projects && !project:
      return (
        <Box p={2}>
          <Projects address={address} />
        </Box>
      );

    case activeTab === SiteTabs.Activity:
      return (
        <Box p={2}>
          <SiteActivityTab addressId={address.addressId} partnerId={address.partnerId} />
        </Box>
      );

    default:
      return null;
  }
};

export const SiteWindowComponent = ({
  isSidePanelExpanded,
  site,
  onCloseSite,
  modeSwitcher,
  activeTab: activeTabProp,
  onActiveTabChange,
}: {
  isSidePanelExpanded: boolean;
  site: Address;
  onCloseSite: () => void;
  modeSwitcher?: React.ReactNode;
  /**
   * URL-driven tab, supplied by the full-page route only. Surfaces without a
   * `:entityTab` param (the Inbox preview pane) omit both props and keep using
   * the persisted store.
   */
  activeTab?: SiteTabs;
  onActiveTabChange?: (tab: SiteTabs) => void;
}) => {
  const { navigate } = useNavigateRoute();
  const { isMobile, isDesktop } = useScreenDimensions();
  const {
    selectedSiteTab,
    setSelectedSiteTab,
    siteSidePanelStateOpen,
    setSiteSidePanelStateOpen,
  } = useSiteViewsStore();

  const activeTab = activeTabProp ?? selectedSiteTab;
  const setActiveTab = onActiveTabChange ?? setSelectedSiteTab;

  const customView = useCustomView(EntityNames.Site);
  const customViewId = customView?.customViewId;

  useEffect(() => {
    if (isMobile) {
      setSiteSidePanelStateOpen(false);
    }
  }, [isMobile, setSiteSidePanelStateOpen]);

  const canResizeDetailsView = siteSidePanelStateOpen && isDesktop;

  const isResizableSplitMode = isSidePanelExpanded && canResizeDetailsView;

  const siteViewRef = useRef<HTMLDivElement | null>(null);

  const { closeTabWith } = useGlobalTabs();

  const closeTab = useCallback(() => {
    closeTabWith(
      (tab) => tab.type === TabType.Site && tab.entity?.id === site.addressId,
      false,
    );
  }, [closeTabWith, site.addressId]);

  const modeSwitcherButton = useMemo(() => {
    return (
      modeSwitcher || (
        <EntityOpenModeSwitcher
          openMode={RecordOpenMode.SidePeek}
          onClick={() => {
            closeTab();
            navigate(
              portalPaths.entity.all({
                entityName: 'sites',
                id: site.addressId,
                customViewId,
              }),
            );
          }}
        />
      )
    );
  }, [modeSwitcher, closeTab, navigate, site.addressId, customViewId]);

  const {
    position: sidePanelWidth,
    separatorProps,
    isDragging,
  } = useResizable({
    axis: 'x',
    initial: RIGHT_PANEL_WIDTH_PX,
    min: RIGHT_PANEL_MIN_WIDTH_PX,
    max: RIGHT_PANEL_MAX_WIDTH_PX,
    containerRef: siteViewRef as RefObject<HTMLElement>,
    reverse: true,
    disabled: !siteSidePanelStateOpen || isMobile,
  });

  const closeSidePanel = useCallback(
    () => setSiteSidePanelStateOpen(false),
    [setSiteSidePanelStateOpen],
  );

  const { copyToClipboard } = useCopyToClipboard();

  const { checkValidity } = useGroupRules();
  const { tabs } = useSiteTabs();

  const { restoreAddress } = useRestoreAddress();

  const onRestoreSiteClick = useCallback(async () => {
    await restoreAddress(site.addressId);
  }, [restoreAddress, site.addressId]);

  const [panelToolsSlot, setPanelToolsSlot] = useState<HTMLElement | null>(null);

  return (
    <SiteContainer
      ref={siteViewRef}
      sx={{
        userSelect: isDragging ? 'none' : 'auto',
      }}
    >
      <BlockContainer sx={{ minWidth: '20rem' }}>
        <BlockHeader>
          <DrawerCloseControl />
          {!isMobile && isSidePanelExpanded ? (
            <GridBreadcrumbItem handleClick={onCloseSite} text={customView?.name || 'All'} />
          ) : null}
          <SiteAddress
            address={site}
            onClick={() => copyToClipboard(site.fullAddress!)}
            tooltipText="Click to copy"
          />
          <Box flexGrow={1} />
          <ViewerListStateful entity="Address" entityId={site?.id} />

          {!isMobile && modeSwitcherButton}
          <SiteMenu address={site} />
          <Tooltip
            title={
              siteSidePanelStateOpen ? CLOSE_DETAILS_PANEL_TOOLTIP : OPEN_DETAILS_PANEL_TOOLTIP
            }
          >
            <DynamicIcon
              name={siteSidePanelStateOpen ? 'right_panel_close' : 'right_panel_open'}
              sx={{ cursor: 'pointer' }}
              onClick={() => setSiteSidePanelStateOpen(!siteSidePanelStateOpen)}
            />
          </Tooltip>
        </BlockHeader>
        <BlockContent
          sx={{
            p: 0,
            minHeight: 0,
            position: 'static',
            overflow: 'visible',
          }}
        >
          {isSidePanelExpanded ? (
            // Full page: SidePanelTabs itself spans content + rail, so the tab strip
            // (and its bottom border) run the full container width, matching the
            // prototype's sd-tabs; content and rail form a row below it and each
            // scrolls independently, with the rail's cards running under the tabs band.
            <SidePanelTabs
              showLabel
              key={site?.addressId}
              activeTab={activeTab}
              setActiveTab={(newValue: SetStateAction<SiteTabs>) => {
                setActiveTab(newValue as SiteTabs);
              }}
              tabs={tabs}
              variant="scrollable"
              tabSx={SidePanelTabStyles}
              innerSx={SIDE_PANEL_TABS_FULL_PAGE_INNER_STYLES}
              hasBorderTop={false}
            >
              <>
                <Stack minHeight={0} flexGrow={1} sx={FULL_PAGE_CONTENT_COLUMN_SX}>
                  <TabContent activeTab={activeTab} address={site} />
                  {site.isDeleted ? (
                    <DeletedRecordAlert
                      entityLabel="site"
                      deletedAt={site.deletedAt}
                      canRestore={checkValidity([
                        UserGroupRules.AccessToRestoreDeletedAddresses,
                      ])}
                      onRestore={onRestoreSiteClick}
                    />
                  ) : null}
                </Stack>

                {isResizableSplitMode ? (
                  <Box
                    sx={{
                      position: 'relative',
                      flexShrink: 0,
                      width: RIGHT_PANEL_RESIZE_GUTTER_WIDTH,
                    }}
                  >
                    <ResizeHandle
                      axis="x"
                      isDragging={isDragging}
                      {...separatorProps}
                      sx={{ left: 0, right: 0, top: 0 }}
                    />
                  </Box>
                ) : null}

                <SiteDetailsWrapper
                  open={siteSidePanelStateOpen}
                  sx={getEntityDetailsRailSx({
                    isMobile,
                    sidePanelWidth,
                    isDragging,
                    isOpen: siteSidePanelStateOpen,
                  })}
                >
                  <Box ref={setPanelToolsSlot} sx={getPanelToolsSlotSx(isMobile)} />
                  <Box
                    sx={{
                      pt: '0.5rem',
                      pr: isMobile ? 0 : '0.25rem',
                      pb: '0.5rem',
                    }}
                  >
                    <SiteSidePanel
                      onClose={closeSidePanel}
                      site={site}
                      extendedView
                      toolsSlot={panelToolsSlot}
                    />
                  </Box>
                </SiteDetailsWrapper>
              </>
            </SidePanelTabs>
          ) : (
            <>
              <Stack flexGrow={1} justifyContent="space-between" minHeight={0}>
                <Stack
                  minHeight={0}
                  sx={{
                    position: 'relative',
                    width:
                      !isMobile && siteSidePanelStateOpen
                        ? `calc(100% - ${Math.max(sidePanelWidth, 0)}px)`
                        : '100%',
                    height: '100%',
                  }}
                >
                  <SidePanelTabs
                    showLabel
                    key={site?.addressId}
                    activeTab={activeTab}
                    setActiveTab={(newValue: SetStateAction<SiteTabs>) => {
                      setActiveTab(newValue as SiteTabs);
                    }}
                    tabs={tabs}
                    variant="scrollable"
                    tabSx={SidePanelTabStyles}
                    innerSx={SidePanelTabsInnerStyles}
                    hasBorderTop={false}
                  >
                    <Stack flexGrow={1} minHeight={0}>
                      <TabContent activeTab={activeTab} address={site} />
                    </Stack>
                  </SidePanelTabs>
                </Stack>

                <SiteDetailsWrapper
                  open={siteSidePanelStateOpen}
                  sx={{
                    width: isMobile ? '100%' : sidePanelWidth,
                    ...(isDragging && { transition: 'none' }),
                    zIndex: isMobile ? 1 : undefined,
                  }}
                >
                  <SiteSidePanel onClose={closeSidePanel} site={site} extendedView />
                </SiteDetailsWrapper>
              </Stack>
              {site.isDeleted ? (
                <DeletedRecordAlert
                  entityLabel="site"
                  deletedAt={site.deletedAt}
                  canRestore={checkValidity([UserGroupRules.AccessToRestoreDeletedAddresses])}
                  onRestore={onRestoreSiteClick}
                />
              ) : null}
            </>
          )}
        </BlockContent>
      </BlockContainer>
    </SiteContainer>
  );
};

export const SiteWindow = memo(SiteWindowComponent);
